import { hierarchy, treemap, treemapSquarify } from 'd3-hierarchy';

import { TREEMAP_LAYOUT, TREEMAP_TEXT_LEVELS, type TreemapTextLevel } from './chart.constant';
import { estimateTextWidth } from './chart.lib';
import type { TreemapGroup, TreemapItem } from './model/treemap.types';

export interface TreemapBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TreemapTile extends TreemapBox {
  item: TreemapItem;
  group: TreemapGroup;
  groupIndex: number;
}

export interface TreemapGroupBox extends TreemapBox {
  group: TreemapGroup;
  groupIndex: number;
  /** 전체 합에 대한 이 구간 합의 비율(0~1). 전체 합이 0 이면 null */
  share: number | null;
}

export interface TreemapLayout {
  tiles: TreemapTile[];
  groups: TreemapGroupBox[];
}

interface Datum {
  group?: TreemapGroup;
  groupIndex?: number;
  item?: TreemapItem;
  children?: Datum[];
}

/**
 * squarify 배치(d3-hierarchy 는 배치 셈에만 쓴다 — DD-54 #1). 넓이 = size, 0 이하 · 숫자가 아닌 항목과 빈 구간은 뺀다.
 * 네모 사이 2px(조각마다 안쪽 1px).
 */
export function treemapLayout(
  groups: readonly TreemapGroup[],
  width: number,
  height: number,
): TreemapLayout {
  const children: Datum[] = groups
    .map((group, groupIndex) => ({
      group,
      groupIndex,
      children: group.items
        .filter((item) => Number.isFinite(item.size) && item.size > 0)
        .map((item) => ({ item })),
    }))
    .filter((datum) => datum.children.length > 0);
  if (children.length === 0 || width <= 0 || height <= 0) return { tiles: [], groups: [] };

  const root = treemap<Datum>()
    .tile(treemapSquarify)
    .size([width, height])
    .paddingInner(TREEMAP_LAYOUT.gap)
    .round(true)(
    hierarchy<Datum>({ children })
      .sum((datum) => datum.item?.size ?? 0)
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0)),
  );

  const total = root.value ?? 0;
  const box = (node: { x0: number; y0: number; x1: number; y1: number }): TreemapBox => ({
    x: node.x0,
    y: node.y0,
    width: node.x1 - node.x0,
    height: node.y1 - node.y0,
  });

  return {
    groups: (root.children ?? []).map((node) => ({
      ...box(node),
      group: node.data.group!,
      groupIndex: node.data.groupIndex!,
      share: total > 0 ? (node.value ?? 0) / total : null,
    })),
    tiles: root.leaves().map((leaf) => ({
      ...box(leaf),
      item: leaf.data.item!,
      group: leaf.parent!.data.group!,
      groupIndex: leaf.parent!.data.groupIndex!,
    })),
  };
}

/** 말줄임 단계에서 최소한 보여야 하는 글자(이름 첫 글자 + …) */
const ELLIPSIS = '…';

/**
 * 네모 글자 단계(TREEMAP_TEXT_LEVELS 의 위에서부터 들어가는 첫 단계). 안 들어가면 null(글자 없음).
 * label · value = 이름 · 값 글자.
 */
export function treemapTextLevel(
  tile: TreemapBox,
  label: string,
  value: string,
): TreemapTextLevel | null {
  const { inset } = TREEMAP_LAYOUT;
  const innerWidth = tile.width - inset * 2;
  const widthOf = (text: string, fontSize: number) => estimateTextWidth(text, fontSize) * 1.05;
  return (
    TREEMAP_TEXT_LEVELS.find((level) => {
      const lines = level.lines === 'both' ? [label, value] : [label];
      if (tile.height < lines.length * level.lineHeight + inset * 2) return false;
      if (level.truncate)
        return widthOf(`${[...label][0] ?? ''}${ELLIPSIS}`, level.fontSize) <= innerWidth;
      return lines.every((line) => widthOf(line, level.fontSize) <= innerWidth);
    }) ?? null
  );
}
